/**
 * An instructor's own courses.
 *
 * Drafts and published courses in one list, because the difference between
 * them is a state, not a place — a teacher thinks "my courses", not "my drafts
 * and separately my published things".
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, apiDelete, apiGet, apiPost } from "../../api/client";
import { useAuth } from "../../auth/AuthContext";
import type { CurriculumSummary } from "../../types";

type CourseSummary = {
  id: string;
  title: string;
  description: string;
  is_published: boolean;
  updated_at: string;
  lesson_count: number;
  /** Who wrote it. Shown to an admin, who is looking at other people's work. */
  author: string;
  /** False on someone else's course — an admin may edit, but should see whose. */
  mine: boolean;
};

export function MyCoursesPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [title, setTitle] = useState("");
  const [notice, setNotice] = useState("");

  const { data: courses, isLoading } = useQuery({
    queryKey: ["my-courses"],
    queryFn: () => apiGet<CourseSummary[]>("/api/courses/mine"),
  });

  // Teaching a subject and writing a course are different things, and this
  // page only ever showed the second. An instructor assigned nine subjects saw
  // "you haven't written a course yet" and reasonably concluded the platform
  // had lost their work. /api/curricula already returns exactly what an
  // instructor is assigned.
  const { data: teaching } = useQuery({
    queryKey: ["curricula"],
    queryFn: () => apiGet<CurriculumSummary[]>("/api/curricula"),
    enabled: user?.role === "instructor",
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["my-courses"] });

  const create = useMutation({
    mutationFn: (t: string) => apiPost<CourseSummary>("/api/courses", { title: t }),
    onSuccess: () => {
      setTitle("");
      setNotice("Course created as a draft. Add lessons, then publish it.");
      refresh();
    },
    onError: (e: ApiError) => setNotice(e.message),
  });

  const publish = useMutation({
    mutationFn: ({ id, published }: { id: string; published: boolean }) =>
      apiPost(`/api/courses/${id}/publish`, { published }),
    onSuccess: (_d, { published }) => {
      setNotice(
        published
          ? "Published — learners can find and enrol on it now."
          : "Withdrawn from the catalogue. Learners already enrolled keep their progress.",
      );
      refresh();
    },
    onError: (e: ApiError) => setNotice(e.message),
  });

  const remove = useMutation({
    mutationFn: (id: string) => apiDelete(`/api/courses/${id}`),
    onSuccess: () => {
      setNotice("Course deleted. Learner progress on its objectives is kept.");
      refresh();
    },
    onError: (e: ApiError) => setNotice(e.message),
  });

  if (isLoading) return <p className="text-muted">Loading courses…</p>;

  // An admin is not an author. They reach this page to oversee what has been
  // written on the platform, so it must not address them as the writer.
  const isAdmin = user?.role === "admin";

  return (
    <div className="my-courses">
      <h1>{isAdmin ? "All courses" : "My courses"}</h1>
      <p className="text-muted">
        {isAdmin
          ? "Every course written on the platform, drafts included. Learners see the author's name and choose accordingly."
          : "Courses you have written. Learners see your name on them and choose accordingly."}
      </p>

      {notice && <div className="notice" role="status">{notice}</div>}

      {/* What you teach, before what you wrote. Most instructors teach the
          platform's CAPS material and write nothing, and for them this is the
          whole answer to "where are my courses". */}
      {!isAdmin && teaching && teaching.length > 0 && (
        <div className="card teaching-set">
          <h2 className="account-section-title">Subjects you teach</h2>
          <p className="text-muted account-section-note">
            Assigned to you by an admin. The material is the platform's, so
            learners see it published by PyDialog — writing your own course is
            below.
          </p>
          <ul className="teaching-list">
            {teaching.map((c) => (
              <li key={c.id}>
                <Link to={`/learn/${c.id}`}>{c.title}</Link>
                <span className="text-muted">
                  {" "}
                  · {c.lesson_count}{" "}
                  {c.lesson_count === 1 ? "lesson" : "lessons"}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="card new-course">
        <label>
          <span className="field-label">Start a new course</span>
          <div className="new-course-row">
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Grade 12 Mathematics — Calculus"
              minLength={3}
            />
            <button
              className="btn btn-primary"
              disabled={title.trim().length < 3 || create.isPending}
              onClick={() => create.mutate(title.trim())}
            >
              {create.isPending ? "Creating…" : "Create draft"}
            </button>
          </div>
        </label>
      </div>

      {!courses?.length ? (
        <div className="card">
          <p className="text-muted" style={{ margin: 0 }}>
            {isAdmin
              ? "Nobody has written a course yet. Instructors write their own; the built-in CAPS material is listed under Curricula."
              : "You haven't written a course of your own yet. Create one above — it stays a draft until you publish it. Any subject assigned to you is listed above and needs nothing from you here."}
          </p>
        </div>
      ) : (
        <div className="card">
          <table>
            <thead>
              <tr>
                <th>Course</th>
                {isAdmin && <th>Author</th>}
                <th>Lessons</th>
                <th>Status</th>
                <th>Updated</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {courses.map((c) => (
                <tr key={c.id}>
                  <td>
                    <strong>{c.title}</strong>
                    {c.description && (
                      <div className="text-muted" style={{ fontSize: "0.82rem" }}>
                        {c.description}
                      </div>
                    )}
                  </td>
                  {isAdmin && <td>{c.author}</td>}
                  <td>{c.lesson_count}</td>
                  <td>
                    <span
                      className={`badge ${c.is_published ? "badge-primary" : ""}`}
                    >
                      {c.is_published ? "published" : "draft"}
                    </span>
                  </td>
                  <td className="text-muted" style={{ fontSize: "0.82rem" }}>
                    {new Date(c.updated_at).toLocaleDateString()}
                  </td>
                  <td>
                    <div className="course-actions">
                      <Link to={`/tutor/courses/${c.id}`} className="btn btn-sm btn-secondary">
                        Edit
                      </Link>
                      <button
                        className="btn btn-sm btn-secondary"
                        disabled={publish.isPending}
                        onClick={() =>
                          publish.mutate({ id: c.id, published: !c.is_published })
                        }
                      >
                        {c.is_published ? "Withdraw" : "Publish"}
                      </button>
                      <button
                        className="btn btn-sm btn-secondary"
                        disabled={remove.isPending}
                        onClick={() => {
                          if (
                            window.confirm(
                              `Delete "${c.title}"? This cannot be undone.`,
                            )
                          ) {
                            remove.mutate(c.id);
                          }
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
